import { Injectable, inject } from '@angular/core';
import { isNullOrUndefined } from '@pineapplelab/util';
import { cloneDeep } from 'lodash';
import firebase from 'firebase/compat/app';
import { DocumentReference } from '@angular/fire/compat/firestore';
import { DbService } from '../../../services/db/db.service';
import { StorageService } from '../../../services/storage/storage.service';
import { LanguageCourseService } from '../../../services/language-course/language-course.service';
import { UniqueIdService } from '../../../services/numeric-id/numeric-id.service';
import { MediaLibraryService } from '../../../services/media-library/media-library.service';
import { ApiService } from '../../../services/api/api.service';
import { Course } from '../../../models/course';
import { LanguageCourse } from '../../../models/languageCourse';
import { Project } from '../../../models/project';
import { calculateChaptersDuration } from './course-duration.util';
import { Category } from '../../../models/category';
import { RoleEnum } from '../../../enums/role.enum';
import { PublishStatusEnum } from '../../../enums/publishStatus.enum';
import { Language } from '../../../utils/language';
import { environment } from '../../../../environments/environment';

@Injectable({
	providedIn: 'root'
})
export class CourseService {

	private db = inject(DbService);
	private storageService = inject(StorageService);
	private languageCourseService = inject(LanguageCourseService);
	private uniqueIdService = inject(UniqueIdService);
	private mediaLibraryService = inject(MediaLibraryService);
	private api = inject(ApiService);

	async save(model: Course, languageCourses: LanguageCourse[], projectId: string) {
		if (!isNullOrUndefined(model?.__fileImage)) {
			model.image = await this.storageService.saveOrRemoveImagesIfNecessary(
				model.__fileImage,
				`${Project.PATH}/${projectId}/${Course.PATH}/${model.id}/${model.__fileImage.name}`,
			);
			model.__fileImage = null;
		}
		const json = model.toJson();

		await this.db.getProyIdRef(projectId)
			.collection(Course.PATH).doc(model.id).set(json, { merge: true });

		for (const language of languageCourses ?? []) {
			if (isNullOrUndefined(language.id)) {
				language.duration = calculateChaptersDuration(language.chapters);
				language.id = this.db.generateFirebaseUniqueId();
			}
			await this.languageCourseService.save(language, projectId, model.id);
		}
		return json;
	}

	async updateById(model: Course, projectId: string) {
		const json = model.toJson();
		await this.db.getProyIdRef(projectId)
			.collection(Course.PATH).doc(model.id).set(json, { merge: true });
	}

	async delete(course: Course, projectId: string) {
		course.isDeleted = true;
		const json = course.toJson();
		await Promise.all([
			this.db.getProyIdRef(projectId)
				.collection(Course.PATH).doc(course.id).set(json, { merge: true }),
			this.removeNestedVideosAssignments(json?.id, projectId),
		]);
	}

	async getAll(projectId: string): Promise<Course[] | null> {
		const snapshot = await this.db.getProyIdRef(projectId).collection(Course.PATH).get();
		if (snapshot.empty) {
			return null;
		}
		return snapshot.docs.map(doc => new Course(doc.data()));
	}

	/**
	 * The category's live courses, in category order. Deleting a course leaves it in every
	 * category's `courses`, so missing and deleted ones are dropped here; otherwise the editor
	 * would load them back and every save would write them into the category again.
	 */
	async getByCategory(category: Category, projectId: string): Promise<Course[]> {
		const courseRefs: DocumentReference[] = category.courses ?? [];
		const snapshots = await Promise.all(courseRefs.map(ref => ref.get()));
		return snapshots
			.filter(snapshot => snapshot.exists && snapshot.data()?.['is_deleted'] !== true)
			.map(snapshot => new Course(snapshot.data()));
	}

	async getById(courseId: string, projectId: string): Promise<Course | null> {
		const snapshot = await this.db.getProyIdRef(projectId)
			.collection(Course.PATH).doc(courseId).get();
		return snapshot.exists ? new Course(snapshot.data()) : null;
	}

	async getByFilter(filter: string, projectId: string): Promise<Course[]> {
		const snapshot = await this.db.getProyIdRef(projectId)
			.collection(Course.PATH)
			.orderBy('name').startAt(filter).endAt(filter + '\uf8ff')
			.get();
		return snapshot.docs.map(doc => new Course(doc.data()));
	}

	async duplicate(model: Course, projectId: string, authUserRole: RoleEnum) {
		const newModel = cloneDeep(model);
		newModel.createdAt = firebase.firestore.Timestamp.now();
		newModel.status = PublishStatusEnum.DRAFT;
		newModel.assigned = false;
		newModel.name = `${model.name} - Copy`;
		newModel.nameLowercase = newModel.name.toLowerCase();
		newModel.id = await this.uniqueIdService.getOrCreateNextNumericId(
			Course.COUNTERCOLLECTION, Course.COUNTER, Course.COUNTERSTART);
		newModel.createdByAdmin = authUserRole === RoleEnum.ADMIN;

		const languageCourses = await this.languageCourseService.getById(model.id, projectId) ?? [];
		newModel.duplicatedFrom = model.id;
		newModel.translationsInherited = languageCourses.some(lc => !lc.isDefault && !lc.isDeleted);
		const videos = languageCourses.flatMap(lc =>
			(lc?.chapters ?? []).map(chapter => chapter?.videoId).filter(Boolean));

		await Promise.all([
			this.save(newModel, languageCourses, projectId),
			this.linkDuplicatedCoursesWithVideos(videos, newModel?.id, projectId),
		]);
	}

	async publish(model: Course, projectId: string) {
		await this.db.getProyIdRef(projectId).collection(Course.PATH).doc(model.id).update({
			status: PublishStatusEnum.PUBLISHED, ready_to_publish: false,
		});
	}

	getRef(id: string, projectId: string): DocumentReference {
		return this.db.getProyIdRef(projectId).collection(Course.PATH).doc(id);
	}

	async translate(
		courseId: string,
		language: Language,
		videoUrls: string[],
		options: { subtitles: boolean; text: boolean },
	): Promise<string | null> {
		let subtitlePromises: Promise<unknown>[] = [];
		if (options?.subtitles) {
			subtitlePromises = (videoUrls ?? [])
				.filter(Boolean)
				.map(url => this.mediaLibraryService.generateVideoSubtitles(url, [language?.code]));
		}
		if (options?.text) {
			const data = {
				course_id: courseId,
				language: language,
				videos_url: videoUrls,
			};
			const [translatedCourse] = await Promise.all([
				this.api.post('/courseLanguages/translate', data),
				...subtitlePromises,
			]);
			return (translatedCourse as any).id;
		}
		await Promise.all(subtitlePromises);
		return null;
	}

	trackTranslationProcess(
		courseId: string,
		translationId: string,
		next: (snapshot: firebase.firestore.DocumentSnapshot<firebase.firestore.DocumentData>) => void,
		error: (error: firebase.firestore.FirestoreError) => void,
		projectId: string = environment.projectId,
	): () => void {
		return this.db.getProyIdRef(projectId)
			.collection(Course.PATH)
			.doc(courseId)
			.collection(LanguageCourse.PATH)
			.doc(translationId)
			.onSnapshot(next, error);
	}

	private async removeNestedVideosAssignments(courseId: string, projectId: string): Promise<void> {
		const languageCourses = await this.languageCourseService.getById(courseId, projectId);
		if (!languageCourses) { return; }
		const videos = languageCourses.flatMap(lc =>
			(lc?.chapters ?? []).map(chapter => chapter?.videoId).filter(Boolean));

		await Promise.all(videos.map(videoId =>
			this.mediaLibraryService.resolveVideoAssignments(videoId, courseId, 'remove', projectId)));
	}

	private async linkDuplicatedCoursesWithVideos(
		videoIds: string[], courseId: string, projectId: string,
	): Promise<void> {
		await Promise.all(videoIds.map(id =>
			this.mediaLibraryService.resolveVideoAssignments(id, courseId, 'add', projectId)));
	}
}
